<script setup lang="ts">
import MazPhoneNumberInput from 'maz-ui/components/MazPhoneNumberInput'

import type { CountryCode } from 'libphonenumber-js'
import type { Results } from 'maz-ui/components/MazPhoneNumberInput'

export interface PhoneObject extends Results {
  internalPhoneNumber: string
}

const props = withDefaults(
  defineProps<{
    countryCode?: CountryCode | null
    modelValue: string | null
    countries?: CountryCode[]
    label?: string
    required?: boolean
    errors?: string
  }>(),
  {
    countryCode: null,
    required: false,
    label: '',
    countries: () => [],
    errors: ''
  }
)
const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:phoneObject': [Results]
  'update:errors': [errors: string]
}>()

const { t } = useI18n()

const bindProps = computed(() => {
  if (props.countries.length) {
    return {
      onlyCountries: props.countries
    }
  }

  return {}
})

const handleChange = (phoneO: Results) => {
  if (phoneO.isValid) {
    emit('update:errors', '')
  } else {
    emit('update:errors', t('global.fieldErrors.invalidPhoneNumber'))
  }

  emit('update:phoneObject', {
    ...phoneO,
    internalPhoneNumber: phoneO?.e164?.replace('+', '')
  } as PhoneObject)
}
</script>

<template>
  <div class="wrap_tel_input relative flex w-full flex-col gap-2">
    <label for="phone" class="block text-sm text-foreground">
      {{ label }}<sup v-if="required" class="ml-1 text-red-600">*</sup>
    </label>

    <MazPhoneNumberInput
      :model-value="modelValue"
      no-search
      :country-code="countryCode"
      :translations="{
        countrySelector: {
          placeholder: t('global.phoneInput.countryCode'),
          error: t('global.phoneInput.error'),
          searchPlaceholder: t('global.phoneInput.searchPlaceholder')
        },
        phoneInput: {
          placeholder: t('global.phoneInput.placeholder'),
          example: t('global.phoneInput.example')
        }
      }"
      v-bind="bindProps"
      @update="handleChange"
      @update:model-value="emit('update:modelValue', $event)"
    />
    <p v-if="errors" class="text-sm text-red-600">
      {{ errors }}
    </p>
  </div>
</template>

<style>
@reference "@/input.css";

.wrap_tel_input .m-input-wrapper {
  width: 100%;
  border-radius: 6px;
  @apply border border-solid border-input! text-foreground! bg-white! focus:border-primary placeholder:text-muted-foreground rounded-md! h-[38px]! !shadow-none;
}
.wrap_tel_input .m-input-wrapper .m-input-wrapper-input {
  @apply text-sm! !bg-white;
}
.wrap_tel_input .m-input-wrapper .m-input-label {
  @apply text-xs! !text-foreground;
}
.wrap_tel_input .m-phone-number-input {
  @apply gap-2;
}
.wrap_tel_input .m-input.--should-up .m-input-label {
  transform: scale(0.8) translateY(-1em);
}
</style>
